import { inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import type { Observable } from 'rxjs';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import Keycloak from 'keycloak-js';
import { environment } from '../../../../../environments/environment';
import { notifDtoToApp } from '../../api/notifications.service';
import type { AppNotification, NotifDto } from '../../../interfaces/notification.interface';

/**
 * Le hub vit à la RACINE de l'API (MapHub("/hubs/notifications")), pas sous /api.
 * environment.baseUrl = ".../api/" → on résout le chemin absolu sur la même origine.
 */
const HUB_URL = new URL('/hubs/notifications', environment.baseUrl).toString();

/** Événement serveur (sans payload) : les droits du citoyen ont changé — défini par l'API (SignalRNotificationService). */
export const DROITS_MIS_A_JOUR_EVENT = 'droitsMisAJour';

const MAX_RECONNECT_DELAY_MS = 30_000;

/** Délai avant la tentative de reconnexion n° (n+1) : immédiat, puis 2 s, 4 s, 8 s… plafonné à 30 s, sans abandon. */
export function reconnectDelayMs(previousRetryCount: number): number {
  return previousRetryCount === 0 ? 0 : Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** previousRetryCount);
}

/**
 * Connexion SignalR temps réel aux notifications du citoyen authentifié.
 * - Authentification : token Keycloak passé en query string (WebSocket ne porte pas de header).
 * - Reconnexion automatique ; en cas d'échec, la cloche reste alimentée par le polling REST.
 * - À chaque notification reçue, émet l'AppNotification mappée sur `incoming$`.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeNotificationsService {
  private readonly keycloak = inject(Keycloak);
  private connection: HubConnection | null = null;

  private readonly incomingSubject = new Subject<AppNotification>();
  /** Émis à chaque notification reçue en temps réel. */
  readonly incoming$: Observable<AppNotification> = this.incomingSubject.asObservable();

  private readonly droitsMisAJourSubject = new Subject<void>();
  /** Émis quand le serveur signale que les droits du citoyen ont changé (renouvellement, suspension…). */
  readonly droitsMisAJour$: Observable<void> = this.droitsMisAJourSubject.asObservable();

  /** État de la connexion temps réel (indicatif). */
  readonly connected = signal(false);

  /** Démarre la connexion au hub (idempotent). À appeler une fois l'utilisateur authentifié. */
  async connect(): Promise<void> {
    if (this.connection) return;

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: async () => {
          // Rafraîchit le token s'il expire dans < 30 s pour ne pas ouvrir une connexion morte.
          try {
            await this.keycloak.updateToken(30);
          } catch {
            /* échec de refresh : on tente avec le token courant, le serveur revalidera */
          }
          return this.keycloak.token ?? '';
        },
      })
      // Reconnexion sans limite (la politique par défaut abandonne après ~42 s, p. ex. pendant un déploiement de l'API).
      .withAutomaticReconnect({nextRetryDelayInMilliseconds: ctx => reconnectDelayMs(ctx.previousRetryCount)})
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection = connection;

    connection.on('notification', (dto: NotifDto) => {
      this.incomingSubject.next(notifDtoToApp(dto));
    });
    connection.on(DROITS_MIS_A_JOUR_EVENT, () => this.droitsMisAJourSubject.next());

    connection.onreconnected(() => {
      this.connected.set(true);
      // Des changements de droits ont pu être émis pendant la coupure : resynchronisation.
      this.droitsMisAJourSubject.next();
    });
    connection.onclose(() => this.connected.set(false));

    try {
      await connection.start();
      this.connected.set(true);
    } catch {
      // Best-effort : on n'échoue pas l'app si le hub est injoignable.
      this.connected.set(false);
    }
  }

  /** Ferme la connexion (déconnexion / logout). */
  async disconnect(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    this.connected.set(false);
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      try {
        await connection.stop();
      } catch {
        /* ignore */
      }
    }
  }
}
